class LuxuryHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-lh-bar]');
    this.veil = this.querySelector('[data-lh-veil]');
    this.toggleButton = this.querySelector('[data-menu-toggle]');
    this.closeButton = this.querySelector('[data-menu-close]');
    this.overlay = this.querySelector('[data-menu-overlay]');
    this.dropdownItems = Array.from(this.querySelectorAll('[data-lh-dropdown]'));
    this.plainNavItems = Array.from(
      this.querySelectorAll('.luxury-header__nav-item:not([data-lh-dropdown])')
    );
    this.scrollThreshold = Number(this.dataset.scrollThreshold || 60);
    this.lastY = window.scrollY;
    this.hoverTimer = null;
    this.closeLockUntil = 0;
    // Brief debounce after opening so hover transitions stay stable (ms)
    this.openHoldMs = 350;

    this.onToggle = this.onToggle.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onBarEnter = this.onBarEnter.bind(this);
    this.onBarLeave = this.onBarLeave.bind(this);
    this.onVeilClick = this.onVeilClick.bind(this);
    this.setHeaderHeight = this.setHeaderHeight.bind(this);

    this.toggleButton?.addEventListener('click', this.onToggle);
    this.closeButton?.addEventListener('click', this.onClose);
    this.veil?.addEventListener('click', this.onVeilClick);
    this.bar?.addEventListener('mouseenter', this.onBarEnter);
    this.bar?.addEventListener('mouseleave', this.onBarLeave);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.setHeaderHeight);
    document.addEventListener('keydown', this.onKeydown);

    this.setupDropdowns();
    this.onScroll();
    this.setHeaderHeight();
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.onToggle);
    this.closeButton?.removeEventListener('click', this.onClose);
    this.veil?.removeEventListener('click', this.onVeilClick);
    this.bar?.removeEventListener('mouseenter', this.onBarEnter);
    this.bar?.removeEventListener('mouseleave', this.onBarLeave);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.setHeaderHeight);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.hoverTimer) window.clearTimeout(this.hoverTimer);
    document.body.classList.remove('lh-dropdown-open');
  }

  isCloseLocked() {
    return Date.now() < this.closeLockUntil;
  }

  setupDropdowns() {
    this.dropdownItems.forEach((item) => {
      const trigger = item.querySelector('[data-lh-dropdown-trigger]');
      const panel = item.querySelector('[data-lh-dropdown-panel]');
      if (!trigger || !panel) return;

      item.addEventListener('mouseenter', () => this.openDropdown(item));
      trigger.addEventListener('focus', () => this.openDropdown(item));
      panel.addEventListener('mouseenter', () => {
        if (this.hoverTimer) {
          window.clearTimeout(this.hoverTimer);
          this.hoverTimer = null;
        }
        this.classList.add('is-hovered');
      });
    });

    this.plainNavItems.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        if (this.isCloseLocked()) return;
        if (this.classList.contains('is-dropdown-open')) {
          this.closeAllDropdowns();
        }
      });
    });
  }

  openDropdown(item) {
    if (this.hoverTimer) {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }

    this.closeLockUntil = Date.now() + this.openHoldMs;

    this.dropdownItems.forEach((other) => {
      if (other === item) return;
      other.classList.remove('is-open');
      other.querySelector('[data-lh-dropdown-trigger]')?.setAttribute('aria-expanded', 'false');
    });

    item.classList.add('is-open');
    item.querySelector('[data-lh-dropdown-trigger]')?.setAttribute('aria-expanded', 'true');
    this.classList.add('is-dropdown-open', 'is-hovered');
    this.classList.remove('is-hidden');
    document.body.classList.add('lh-dropdown-open');
  }

  closeAllDropdowns() {
    if (this.isCloseLocked()) return;

    this.dropdownItems.forEach((item) => {
      item.classList.remove('is-open');
      item.querySelector('[data-lh-dropdown-trigger]')?.setAttribute('aria-expanded', 'false');
    });
    this.classList.remove('is-dropdown-open');
    document.body.classList.remove('lh-dropdown-open');
  }

  forceCloseDropdowns() {
    this.closeLockUntil = 0;
    if (this.hoverTimer) {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
    this.dropdownItems.forEach((item) => {
      item.classList.remove('is-open');
      item.querySelector('[data-lh-dropdown-trigger]')?.setAttribute('aria-expanded', 'false');
    });
    this.classList.remove('is-dropdown-open');
    document.body.classList.remove('lh-dropdown-open');
  }

  onVeilClick() {
    this.forceCloseDropdowns();
    this.classList.remove('is-hovered');
  }

  onToggle(event) {
    event?.stopPropagation();
    const willOpen = !this.classList.contains('is-menu-open');
    if (willOpen) this.openMenu();
    else this.closeMenu();
  }

  onClose(event) {
    event?.stopPropagation();
    this.closeMenu();
  }

  openMenu() {
    this.classList.add('is-menu-open');
    this.classList.remove('is-hidden');
    this.overlay?.removeAttribute('hidden');
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('menu-open');
  }

  closeMenu() {
    this.classList.remove('is-menu-open');
    this.overlay?.setAttribute('hidden', '');
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.closeMenu();
      this.forceCloseDropdowns();
    }
  }

  onBarEnter() {
    if (this.hoverTimer) window.clearTimeout(this.hoverTimer);
    this.classList.add('is-hovered');
  }

  onBarLeave() {
    const wait = Math.max(180, this.closeLockUntil - Date.now());

    if (this.hoverTimer) window.clearTimeout(this.hoverTimer);
    this.hoverTimer = window.setTimeout(() => {
      if (this.isCloseLocked()) return;
      if (this.bar?.matches(':hover')) return;

      this.closeAllDropdowns();
      if (!this.classList.contains('is-menu-open')) {
        this.classList.remove('is-hovered');
      }
    }, wait);
  }

  onScroll() {
    const y = window.scrollY || 0;
    const goingDown = y > this.lastY;
    const atTop = y <= this.scrollThreshold;

    if (this.classList.contains('is-menu-open') || this.classList.contains('is-dropdown-open')) {
      this.classList.remove('is-hidden');
      this.lastY = y;
      return;
    }

    if (atTop) {
      this.classList.add('is-at-top');
      this.classList.remove('is-frosted', 'is-hidden');
    } else if (goingDown) {
      this.classList.remove('is-at-top', 'is-frosted');
      this.classList.add('is-hidden');
    } else {
      this.classList.remove('is-at-top', 'is-hidden');
      this.classList.add('is-frosted');
    }

    this.lastY = y;
    this.setHeaderHeight();
  }

  // Kept for luxury-hero compatibility (no-op visual now)
  setBrandArrived() {}

  setHeaderHeight() {
    const height = this.bar?.offsetHeight || this.offsetHeight || 64;
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${height}px`);
  }
}

if (!customElements.get('luxury-header')) {
  customElements.define('luxury-header', LuxuryHeader);
}
